/**
 * O polling dos jobs de IA assíncronos: `POST /finops/discovery/gerar-ia` (`2d`) e
 * `POST /compliance/padroes/{id}/sugestoes/analisar` (`3d`) — mesmo contrato.
 *
 * 🔒 BR-25 — a geração por IA é SEMPRE por clique; este módulo só continua um
 * job que o clique já disparou, nunca inicia um sozinho.
 *
 * Mesmo padrão de `src/assistente/enviarMensagem.ts` (o chat resolve exatamente
 * este problema: POST aceito, depois aguardar um job assíncrono antes de mostrar
 * o resultado) — replicado aqui, não reinventado. Diferença deliberada: sem
 * `AbortSignal`. O chat é abortável porque o usuário pode fechar o painel no meio
 * da espera; a tela de Discovery não tem um ponto de cancelamento natural hoje
 * (o job roda no servidor de qualquer forma, e a tela continua visível) — se
 * isso mudar, dá pra adicionar o mesmo `signal` do chat sem reescrever o resto.
 */
import { apiUser } from '@/api/clients/apiUser'
import { ErroDeApi } from '@/api/clients/criarCliente'
import { execucaoIaAceitaSchema, statusDaExecucaoIaSchema } from '@/api/contracts/discovery'

export class FalhaNaGeracaoPorIa extends Error {}

interface Polling {
  /** Espera antes de cada consulta; a última se repete. */
  esperasMs: readonly number[]
  /** Teto no cliente — rede para quando a CONSULTA não chega, não quando o job demora. */
  tetoMs: number
  /** Falhas SEGUIDAS de rede ou 5xx toleradas numa consulta de status. */
  falhasToleradas: number
}

const POLLING_PADRAO: Polling = {
  // Medido em docs/teste_fluxos_ia_extra.py (backend): ~15-50s num caso real.
  esperasMs: [2000, 3000, 5000, 8000],
  tetoMs: 90_000,
  falhasToleradas: 5,
}

let polling: Polling = POLLING_PADRAO

/** Só para a suíte: zera as esperas. `null` devolve o padrão. */
export function definirPollingDaExecucaoIaParaTeste(valor: Partial<Polling> | null): void {
  polling = valor ? { ...POLLING_PADRAO, ...valor } : POLLING_PADRAO
}

function dormir(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export interface ResultadoDaGeracaoPorIa {
  quantidadeSugestoes: number
}

/** Como iniciar e acompanhar UM tipo de job de IA (Discovery-IA, análise do `3d`…). */
export interface JobDeIa {
  /** POST que dispara o job (já com query/corpo); devolve o corpo do 202. */
  disparar: (idempotencyKey: string) => Promise<unknown>
  /** Caminho do GET de status para um `idExecucao`. */
  caminhoDoStatus: (idExecucao: string) => string
  idCliente: string
  /** Mensagem quando o teto do cliente estoura — o job pode terminar sozinho depois. */
  mensagemDeDemora: string
}

/**
 * Dispara o job e só resolve quando ele termina (`concluida` ou `falha`) —
 * nunca no 202. Compartilhado pelo Discovery-IA (`2d`) e pela análise de
 * reaproveitamento (`3d`): o contrato do backend é o mesmo molde (Tiago, 19/10).
 */
export async function executarJobDeIa(job: JobDeIa): Promise<ResultadoDaGeracaoPorIa> {
  // Uma chave por CLIQUE. Duplo-clique não pode custar dois jobs de Bedrock.
  const aceita = execucaoIaAceitaSchema.parse(await job.disparar(crypto.randomUUID()))
  return aguardarExecucao(job, aceita.idExecucao)
}

/**
 * Dispara `POST /finops/discovery/gerar-ia` e só resolve quando o job termina.
 */
export async function gerarRecomendacoesPorIa(idCliente: string): Promise<ResultadoDaGeracaoPorIa> {
  return executarJobDeIa({
    idCliente,
    disparar: (chave) =>
      apiUser.post<unknown>('/finops/discovery/gerar-ia', { idCliente }, { headers: { 'Idempotency-Key': chave } }),
    caminhoDoStatus: (idExecucao) => `/finops/discovery/execucoes/${encodeURIComponent(idExecucao)}`,
    mensagemDeDemora:
      'A geração por IA está demorando mais do que o esperado. As recomendações podem aparecer sozinhas em instantes — tente consultar de novo.',
  })
}

async function aguardarExecucao(job: JobDeIa, idExecucao: string): Promise<ResultadoDaGeracaoPorIa> {
  const inicio = Date.now()
  let falhasSeguidas = 0

  for (let consulta = 0; ; consulta += 1) {
    const espera = polling.esperasMs[Math.min(consulta, polling.esperasMs.length - 1)] ?? 0
    if (Date.now() - inicio + espera >= polling.tetoMs) {
      throw new FalhaNaGeracaoPorIa(job.mensagemDeDemora)
    }
    await dormir(espera)

    let bruto: unknown
    try {
      bruto = await apiUser.get<unknown>(job.caminhoDoStatus(idExecucao), {
        params: { idCliente: job.idCliente },
      })
    } catch (erro) {
      const status = erro instanceof ErroDeApi ? erro.status : 0
      // 429 é "consulte mais devagar", nunca "o job falhou" — mesmo raciocínio
      // do chat: volta a esperar, quem encerra uma espera sem fim é o teto.
      if (status === 429) continue

      const transitoria = status === 0 || status >= 500
      falhasSeguidas += 1
      if (transitoria && falhasSeguidas <= polling.falhasToleradas) continue
      throw new FalhaNaGeracaoPorIa('Não foi possível confirmar o resultado da geração por IA agora.')
    }
    falhasSeguidas = 0

    const lido = statusDaExecucaoIaSchema.safeParse(bruto)
    if (!lido.success) throw new FalhaNaGeracaoPorIa('Resposta inesperada do servidor.')

    const estado = lido.data
    if (estado.status === 'processando') continue
    if (estado.status === 'falha') {
      throw new FalhaNaGeracaoPorIa(estado.erroMensagem || 'A geração por IA falhou.')
    }
    return { quantidadeSugestoes: estado.quantidadeSugestoes }
  }
}
